// apps/web/src/pages/pcp/op-os-list.tsx — Fase 02, Plano 04 (OPS-02, D-05).
import { type ReactElement } from 'react';

export interface OsRow {
  readonly id: string;
  readonly opId: string;
  readonly etapaOrdem: number;
  readonly celulaId: string;
  readonly status: string;
  readonly descricao?: string;
  readonly etapaAnteriorOsId: string | null;
  readonly createdAt: string;
}

interface OpOsListProps {
  readonly oss: readonly OsRow[];
  readonly loading?: boolean;
}

export function OpOsList({ oss, loading }: OpOsListProps): ReactElement {
  if (loading) {
    return <div className="py-8 text-center text-sm text-slate-500">Carregando etapas da OP...</div>;
  }

  if (!oss || oss.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        Nenhuma Ordem de Serviço gerada. Libere a OP para materializar as etapas do roteiro.
      </div>
    );
  }

  const getStatusBadge = (os: OsRow) => {
    const status = os.status;
    switch (status.toUpperCase()) {
      case 'ELEGIVEL':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800">Elegível no Coletor</span>;
      case 'EM_OPERACAO':
      case 'EM_EXECUCAO':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-600 text-white">Em Operação</span>;
      case 'FINALIZADA':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700">Finalizada</span>;
      case 'CONCLUIDA':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">Concluída</span>;
      case 'CANCELADA':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700">Cancelada</span>;
      case 'PAUSADA':
      case 'PAUSADO':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">Pausada</span>;
      case 'AGUARDANDO':
      case 'PENDENTE': {
        const anterior = oss.find((etapa) => etapa.id === os.etapaAnteriorOsId);
        const aguardandoAnterior = os.etapaAnteriorOsId !== null && !['CONCLUIDA', 'FINALIZADA'].includes(anterior?.status.toUpperCase() ?? '');
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-500">{aguardandoAnterior ? 'Aguardando Etapa Anterior' : 'Aguardando início'}</span>;
      }
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-500">Estado não reconhecido: {status}</span>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
          {oss.length === 1 ? '1 Ordem de Serviço (OS)' : `${oss.length} Ordens de Serviço (OS)`}
        </h3>
        <span className="text-xs text-slate-500">Sequência do snapshot congelado</span>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 text-xs uppercase tracking-wider">
            <tr>
              <th className="p-3 w-16">Etapa</th>
              <th className="p-3">Descrição / Operação</th>
              <th className="p-3">Célula / Posto</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Etapa Anterior</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {oss.map((os) => (
              <tr key={os.id} className="hover:bg-slate-50">
                <td className="p-3 font-mono font-bold text-slate-900">#{os.etapaOrdem}</td>
                <td className="p-3 font-medium text-slate-800">{os.descricao || `Etapa ${os.etapaOrdem}`}</td>
                <td className="p-3 font-mono text-slate-600 text-xs">{os.celulaId}</td>
                <td className="p-3">{getStatusBadge(os)}</td>
                <td className="p-3 text-xs text-slate-400 font-mono">
                  {os.etapaAnteriorOsId ? `OS anterior vinculada` : 'Primeira etapa (início)'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
